import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle } from "lucide-react";

const LOGO_URL = "https://www.perfinco.pt/wp-content/uploads/2022/07/perfinco-logo.png";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`, { skipAuthRefresh: true })
      .then(() => setValid(true))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("A palavra-passe deve ter pelo menos 6 caracteres."); return; }
    if (password !== confirm) { setError("As palavras-passe não coincidem."); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password }, { skipAuthRefresh: true });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen paper-grain flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <img src={LOGO_URL} alt="Perfinco" className="h-8" />
          <span className="h-6 w-px bg-[color:var(--border)]" />
          <span className="font-display text-sm font-medium tracking-[0.18em] uppercase text-[color:var(--ink-muted)]">Online</span>
        </Link>

        <div className="card-soft" data-testid="reset-password-card">
          {checking ? (
            <p className="text-[color:var(--ink-muted)] text-center py-6">A validar link…</p>
          ) : done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="mx-auto text-[#15803D]" size={36} />
              <h1 className="font-display text-2xl mt-3">Palavra-passe alterada</h1>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">A redirecionar para o início de sessão…</p>
            </div>
          ) : !valid ? (
            <div className="text-center py-4" data-testid="reset-invalid">
              <AlertCircle className="mx-auto text-[#991B1B]" size={36} />
              <h1 className="font-display text-2xl mt-3">Link inválido ou expirado</h1>
              <p className="text-sm text-[color:var(--ink-muted)] mt-2">O link de recuperação já não é válido. Peça um novo na página de início de sessão.</p>
              <Link to="/login" className="btn-primary inline-block mt-5" data-testid="reset-back-login">Voltar ao login</Link>
            </div>
          ) : (
            <>
              <span className="label-eyebrow">recuperar acesso</span>
              <h1 className="font-display text-2xl sm:text-3xl mt-2 mb-6">Definir nova palavra-passe</h1>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <label className="label-eyebrow block mb-2">Nova palavra-passe</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-soft" required minLength={6} data-testid="reset-password-input" autoComplete="new-password" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Confirmar palavra-passe</label>
                  <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-soft" required minLength={6} data-testid="reset-confirm-input" autoComplete="new-password" />
                </div>
                {error && (
                  <div className="text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3" data-testid="reset-error">{error}</div>
                )}
                <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="reset-submit-btn">
                  {saving ? "A guardar…" : "Guardar nova palavra-passe"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
